import {Injectable} from '@angular/core';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {filter, map} from 'rxjs/operators';
import {Serializer, SerializerResponse} from '../../interfaces/serializer';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {SitesService} from '../../services/sites.service';
import {WebsocketService} from '../../services/websocket.service';
import {WebsocketEvent} from '../../interfaces/websocket';
import * as metrics from './metricsConsentAutoCorrection.actions'
import * as sites from "../sites/sites.actions";
import {showSnackbar} from '../snackbar/snackbar.actions';

@Injectable()
export class MetricsConsentAutoCorrectionEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _enable$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(metrics.enableMetricsConsentAutoCorrection),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string> & { default_rate: number }, Serializer<Site>]): Observable<SerializerResponse<Serializer<Site>>> => this._datasetService.toggleConsent(dataset.id, true)),
    switchMap((response: { success: boolean, auto_correct_toggle: boolean }) => [
      metrics.enableMetricsConsentAutoCorrectionSuccess(),
      sites.toggleConsentSuccess({ resp: response }),
      showSnackbar({
        icon: 'auto_fix_normal',
        iconClass: 'green',
        message: 'settings.consent_autocorrect.snackbar.enabled'
      })
    ])
  ));

  private readonly _disable$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string>> => this._actions$.pipe(
    ofType(metrics.disableMetricsConsentAutoCorrection),
    withLatestFrom(this._dataset$),
    switchMap(([_,dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializer<Site>>> => this._datasetService.toggleConsent(dataset.id, false)),
    switchMap((response: { success: boolean, auto_correct_toggle: boolean }) => [
      metrics.disableMetricsConsentAutoCorrectionSuccess(),
      sites.toggleConsentSuccess({ resp: response }),
      showSnackbar({
        icon: 'auto_fix_off',
        iconClass: 'red',
        message: 'settings.consent_autocorrect.snackbar.disabled'
      })
    ])
  ));

  private readonly _init$: Observable<TypedAction<string> & { status: 'not-configured' | 'off' | 'on' }> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(metrics.initMetricsConsentAutoCorrection),
    withLatestFrom(this._dataset$),
    map(([_, dataset]: [TypedAction<string>, Serializer<Site>]): TypedAction<string> & { status: 'not-configured' | 'off' | 'on' } => {
      let status: 'not-configured' | 'off' | 'on' = 'not-configured';
      let buttonIcon: 'auto_fix_off' | 'auto_fix_normal' = 'auto_fix_off'

      if (dataset.attributes.parameters['auto_correct_consent'] !== undefined && ![0, null, undefined].includes(dataset.attributes.parameters['auto_correct_consent']['default_rate'])) {
        status = dataset.attributes.parameters['auto_correct_consent']['auto_correct_toggle'] && 'on' || 'off';

        if (status === 'on') {
          buttonIcon = 'auto_fix_normal';
        }
      }

      return metrics.initMetricsConsentAutoCorrectionSuccess({
        status: status,
        buttonIcon: buttonIcon
      })
    })
  ));

  private readonly _listenWebsocket$: Observable<TypedAction<string> & { processing: boolean }> = createEffect(() => this._actions$.pipe(
    ofType(metrics.listenWebsocketMetricsConsentAutoCorrection),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<WebsocketEvent> => this._websocketService.get('DatasetChannel', dataset.id)),
    map((wsData: WebsocketEvent): TypedAction<string> & { processing: boolean } => {
      let processing: boolean = false;

      if (wsData.message['consent'] && wsData.message['consent'] == 'done') {
        processing = false;
      }

      if (wsData.message['consent'] && wsData.message['consent'] == 'processing') {
        processing = true;
      }

      return metrics.websocketUpdateMetricsConsentAutoCorrection({
        processing: processing
      })
    })
  ));

  private readonly _datasetChange$: Observable<Array<TypedAction<string> & any>> = createEffect(() => this._dataset$.pipe(
    filter((dataset: Serializer<Site>): boolean => !!dataset),
    switchMap((): Array<TypedAction<string> & any> => [
      metrics.initMetricsConsentAutoCorrection(),
      metrics.listenWebsocketMetricsConsentAutoCorrection()
    ])
  ));

  constructor(
    private readonly _store: Store<AppState>,
    private readonly _actions$: Actions,
    private readonly _datasetService: SitesService,
    private readonly _websocketService: WebsocketService
  ) {}

}
